import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../AuthContext';
import useAssuranceSettled from '../auth/useAssuranceSettled';
import useCompanyAccess from './useCompanyAccess';
import { startCompanyAccessLive } from './companyAccessLive';
import { getMyBillingStatus } from '../../services/billing/billingApi';
import { deriveBillingState } from '../../utils/billing/billingState';


/**
 * KAN-196 — the company's billing status for the Plan & billing page and the
 * suspended screen (owner/admin only; the server rejects everyone else, so no
 * other role ever calls it). Loads on mount; refetches on the same
 * `company_access_pings` signal the access level uses (a Stripe webhook bumps
 * it, so an open screen updates in ~2s with no reload) and on resume/focus.
 * A failed refetch keeps the last answer. Not cached across sign-outs.
 *
 * @returns {{ status: object|null, loadState: 'loading'|'ready'|'error', billing: ReturnType<typeof deriveBillingState>,
 *   level: string, isOwner: boolean, reload: () => Promise<object|null> }}
 */
export default function useBillingStatus() {
  const { role, tenantId, sessionUser } = useAuth();
  const { settled } = useAssuranceSettled(sessionUser?.id);
  const { level } = useCompanyAccess();
  const enabled = Boolean(role === 'admin' && tenantId && sessionUser?.id && settled);
  const [state, setState] = useState({ status: null, loadState: 'loading' });
  const alive = useRef(true);

  const load = useCallback(async () => {
    try {
      const status = await getMyBillingStatus();
      if (alive.current) setState({ status, loadState: 'ready' });
      return status;
    } catch (_e) {
      if (alive.current) setState((s) => ({ status: s.status, loadState: s.status ? 'ready' : 'error' }));
      return null;
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    if (!enabled) return undefined;
    void load();
    const stopLive = startCompanyAccessLive(tenantId, () => { void load(); });
    // Resume backup: companyAccessLive re-runs this reader once per resume (KAN-1044).
    return () => {
      alive.current = false;
      stopLive();
    };
  }, [enabled, tenantId, load]);

  // KAN-855: `ownerName` is always present (null when the server has none), so callers never branch on undefined.
  const status = useMemo(() => (state.status ? { ...state.status, ownerName: state.status.ownerName || null, hasOwner: state.status.hasOwner !== false } : null), [state.status]);
  const billing = useMemo(() => deriveBillingState(state.status, level), [state.status, level]);
  return {
    status, loadState: enabled ? state.loadState : 'loading', billing, level, isOwner: state.status?.isOwner === true, reload: load,
  };
}
